import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import './Login.css';
import GoogleLogin from './GoogleLogin';
import { AuthContext } from './AuthContext';
import logo from '../../assets/logo.png';
import ImageCarousel from '../UI/ImageCarousel';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [message, setMessage] = useState({ type: '', text: '' });
    const [isButtonLoading, setIsButtonLoading] = useState(false);
    const [isLinkLoading, setIsLinkLoading] = useState(false);
    const { setIsAuthenticated, setUser } = useContext(AuthContext);
    const navigate = useNavigate();

    // Function to handle login errors and display appropriate messages
    const handleLoginError = (error) => {
        console.error('Login error:', error);
        
        // Get HTTP error code and message
        const statusCode = error.response?.status;
        const errorMessage = error.response?.data?.msg;
        
        // Specific error messages based on status code
        switch (statusCode) {
            case 400:
                setMessage({
                    type: 'error',
                    text: 'Please enter both your email and password to login.'
                });
                break;
            case 401:
                if (errorMessage?.includes('No account found')) {
                    setMessage({
                        type: 'error',
                        text: 'No account found with this email. Would you like to create an account?',
                        actionText: 'Sign up',
                        action: () => navigate('/signup')
                    });
                } else {
                    setMessage({
                        type: 'error',
                        text: 'Incorrect password. Please try again or use the login link option below.'
                    });
                }
                break;
            case 403:
                setMessage({
                    type: 'error',
                    text: 'Your account is not activated. Please check your email for the activation link or request a new one.',
                    actionText: 'Send new activation link',
                    action: () => handleSendLoginLink()
                });
                break;
            case 500:
                setMessage({
                    type: 'error',
                    text: 'Server error occurred. Please try again later or contact support if the problem persists.'
                });
                break;
            default:
                setMessage({
                    type: 'error',
                    text: errorMessage || 'An error occurred while logging in. Please try again.'
                });
        }
    };

    // Function to handle send link errors
    const handleSendLinkError = (error) => {
        console.error('Send login link error:', error);
        
        const statusCode = error.response?.status;
        const errorMessage = error.response?.data?.msg;
        
        switch (statusCode) {
            case 404:
                setMessage({
                    type: 'error',
                    text: 'No account found with this email. Would you like to create an account?',
                    actionText: 'Sign up',
                    action: () => navigate('/signup')
                });
                break;
            default:
                setMessage({
                    type: 'error',
                    text: errorMessage || 'Failed to send login link. Please try again or contact support.'
                });
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setIsButtonLoading(true);
        setMessage({ type: '', text: '' }); // Clear previous messages
        
        try {
            const response = await api.post('/login', { email, password });
            
            // Store the token in localStorage
            localStorage.setItem('token', response.data.access_token);
            
            // Update authentication context
            setIsAuthenticated(true);
            
            // Get user info
            const userResponse = await api.get('/user_info');
            setUser(userResponse.data);
            
            // Success message before redirection
            setMessage({
                type: 'success',
                text: 'Login successful! Redirecting to the application...'
            });
            
            // Redirect to create page (with a small delay to see the message)
            setTimeout(() => {
                navigate('/create');
            }, 800);
        } catch (error) {
            handleLoginError(error);
        } finally {
            setIsButtonLoading(false);
        }
    };

    const handleSendLoginLink = async () => {
        if (!email) {
            setMessage({
                type: 'error',
                text: 'Please enter your email address to receive a login link.'
            });
            return;
        }

        setIsLinkLoading(true);
        setMessage({ type: '', text: '' }); // Clear previous messages
        
        try {
            await api.post('/send_login_link', { email });
            setMessage({
                type: 'success',
                text: `A login link has been sent to ${email}. Please check your inbox and spam folder.`
            });
        } catch (error) {
            handleSendLinkError(error);
        } finally {
            setIsLinkLoading(false);
        }
    };

    return (
        <>
        <ImageCarousel />
        <div className="login-page">
            
            <div className="login-container">
                <img src={logo} alt="AIvatar Logo" className="login-logo" />
                <h2>Login to your account</h2>
                <p>Welcome back! Please login to your account</p>

                <form onSubmit={handleLogin}>
                    <div className="input-group">
                        <div className="icon-container">
                            <span className="material-symbols-outlined login-icon">mail</span>
                        </div>
                        <input
                            type="email"
                            name="email"
                            placeholder="your@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                    </div>
                    <div className="input-group">
                        <div className="icon-container">
                            <span className="material-symbols-outlined login-icon">lock</span>
                        </div>
                        <input
                            type="password"
                            name="password"
                            placeholder="Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                        />
                    </div>

                    <div style={{ marginTop: '30px' }}>
                        <button 
                            className="button-plain" 
                            type="submit" 
                            disabled={isButtonLoading}
                        >
                            <span className="material-symbols-outlined">login</span>
                            {isButtonLoading ? 'Logging in...' : 'Login'}
                        </button>
                    </div>
                </form>

                {message.text && (
                    <div className={message.type === 'error' ? 'error-message' : 'success-message'}>
                        {message.text}
                        {message.action && (
                            <div className="error-action">
                                <button className="button-link" onClick={message.action}>
                                    {message.actionText}
                                </button>
                            </div>
                        )}
                    </div>
                )}

                <div className="login-separator">
                    <span>or</span>
                </div>

                <div className="login-options">
                    <button 
                        className="button-link" 
                        onClick={handleSendLoginLink} 
                        disabled={isLinkLoading}
                    >
                        {isLinkLoading ? 'Sending...' : 'Send me a login link'}
                    </button>
                </div>

                <p className="login-toggle-text">
                    Don't have an account? <strong onClick={() => navigate('/signup')}>Sign up</strong>
                </p>

                <div className="login-separator">
                    <span>or</span>
                </div>

                <GoogleLogin />
            </div>
        </div>
        </>
    );
};

export default Login; 